import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  LinearProgress,
  IconButton,
  Grid,
  Card,
  CardMedia,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Autorenew, CloudUpload, Delete, Visibility } from '@mui/icons-material';
import { uploadDropzoneSx, uploadDropzoneCloudIconSx } from '../utils/dialogFormLayout';

const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4000' 
  : '';

const SetPhotoManager = ({ setId, photos = [], onPhotosUpdated }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const fileInputRef = useRef();

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
    setError('');
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    setSelectedFiles(files);
    setError('');
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select files to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');

    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append('photos', file);
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/sets/${setId}/photos`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload photos');
      }

      const updatedSet = await response.json();
      setSelectedFiles([]);
      setUploadProgress(100);
      
      if (onPhotosUpdated) {
        onPhotosUpdated(updatedSet);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRegenerateVideoPreview = async (photo) => {
    setRegeneratingId(photo._id);
    setError('');
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/sets/${setId}/photos/${photo._id}/regenerate-preview`,
        { method: 'POST' }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to regenerate preview');
      }
      const updatedSet = await response.json();
      if (onPhotosUpdated) {
        onPhotosUpdated(updatedSet);
      }
    } catch (err) {
      setError(err.message || 'Failed to regenerate preview');
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleDeletePhoto = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sets/${setId}/photos/${photoToDelete._id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete photo');
      }

      const updatedSet = await response.json();
      if (onPhotosUpdated) {
        onPhotosUpdated(updatedSet);
      }
      
      setPhotoToDelete(null);
      setDeleteDialogOpen(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const openFileDialog = () => {
    fileInputRef.current.click();
  };

  const openDeleteDialog = (photo) => {
    setPhotoToDelete(photo);
    setDeleteDialogOpen(true);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Photos & Videos ({photos.length})
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Upload Area */}
      <Box
        sx={(theme) => uploadDropzoneSx(theme, { mb: 3 })}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <CloudUpload sx={uploadDropzoneCloudIconSx} />
        <Typography variant="body1" color="text.secondary">
          Drag and drop photos and videos here, or click to select files
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Supports: JPG, PNG, GIF, MP4, MOV, AVI (max 30MB each)
        </Typography>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Selected Files Preview */}
      {selectedFiles.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Selected Files ({selectedFiles.length}):
          </Typography>
          <Grid container spacing={1}>
            {selectedFiles.map((file, index) => {
              const isVideo = file.type.startsWith('video/');
              return (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Card>
                    {isVideo ? (
                      <CardMedia
                        component="video"
                        height="120"
                        src={URL.createObjectURL(file)}
                        sx={{ objectFit: 'cover' }}
                        controls={false}
                      />
                    ) : (
                      <CardMedia
                        component="img"
                        height="120"
                        image={URL.createObjectURL(file)}
                        alt={file.name}
                        sx={{ objectFit: 'cover' }}
                      />
                    )}
                    <CardActions sx={{ p: 1 }}>
                      <Typography variant="caption" noWrap sx={{ flexGrow: 1 }}>
                        {file.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeFile(index)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading}
              fullWidth
            >
              {uploading ? 'Uploading...' : 'Upload Files'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Upload Progress */}
      {uploading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Uploading... {uploadProgress}%
          </Typography>
        </Box>
      )}

      {/* Existing Photos */}
      {photos.length > 0 && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Existing Photos & Videos:
          </Typography>
          <Grid container spacing={2}>
            {photos.map((photo) => {
              // Check if it's a video by checking file extension in URL or name
              const isVideo = photo.imageUrl.match(/\.(mp4|mov|avi|webm|mkv)$/i) || 
                              photo.name.match(/\.(mp4|mov|avi|webm|mkv)$/i);
              return (
                <Grid item xs={6} sm={4} md={3} key={photo._id}>
                  <Card>
                    <CardMedia
                      component="img"
                      height="200"
                      image={photo.thumbnailUrl || photo.imageUrl}
                      alt={photo.name}
                      sx={{ objectFit: 'cover' }}
                      key={`${photo._id}-${photo.updatedAt || ''}-${photo.thumbnailUrl || ''}`}
                    />
                    <CardActions sx={{ p: 1, flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography variant="caption" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                        {photo.name}
                        {isVideo ? ' (video)' : ''}
                      </Typography>
                      {isVideo && (
                        <IconButton
                          size="small"
                          title="Regenerate square preview"
                          disabled={regeneratingId === photo._id}
                          onClick={() => handleRegenerateVideoPreview(photo)}
                        >
                          <Autorenew />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => window.open(photo.imageUrl, '_blank')}
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openDeleteDialog(photo)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Photo</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{photoToDelete?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeletePhoto} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SetPhotoManager; 